import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { findProduct } from "../data/products";

const CartContext = createContext(null);

const load = () => {
  try {
    return JSON.parse(localStorage.getItem("zlv_cart")) || [];
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(load);

  useEffect(() => {
    localStorage.setItem("zlv_cart", JSON.stringify(items));
  }, [items]);

  const add = (slug, qty = 1, silent = false) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.slug === slug);
      if (existing) {
        return prev.map((i) => (i.slug === slug ? { ...i, qty: Math.min(i.qty + qty, 20) } : i));
      }
      return [...prev, { slug, qty }];
    });
    if (!silent) {
      const p = findProduct(slug);
      toast.success(`${p ? p.name : "Item"} added to your bag`);
    }
  };

  const remove = (slug) => setItems((prev) => prev.filter((i) => i.slug !== slug));

  const setQty = (slug, qty) => {
    if (qty < 1) return remove(slug);
    setItems((prev) => prev.map((i) => (i.slug === slug ? { ...i, qty: Math.min(qty, 20) } : i)));
  };

  const clear = () => setItems([]);

  const detailed = useMemo(
    () =>
      items
        .map((i) => ({ ...i, product: findProduct(i.slug) }))
        .filter((i) => i.product),
    [items]
  );

  const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);
  const subtotal = useMemo(
    () => detailed.reduce((s, i) => s + i.product.price * i.qty, 0),
    [detailed]
  );

  return (
    <CartContext.Provider value={{ items, detailed, count, subtotal, add, remove, setQty, clear }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
